// React imports
import { FC, MouseEvent, useEffect, useMemo, useRef } from "react";

// UI Components
import { Sidebar } from "shared/ui";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { DialerCloseButton as CloseButton } from "@/components/shared/widgets/buttons/close-button";

// Contexts & Providers
import { DialerV2Provider } from "@/context/dialer-v2";
import { defaultContextObject } from "@/context/dialer-v1/default-context";

// Hooks
import { useAccountDetails } from "@/hooks/use-account-details";
import { useDialerGlobalContext } from "@/hooks/dialer/use-dialer-global-context";
import { useDialerCallStatus } from "@/hooks/dialer/use-dialer-call-status";
import { useMaxedDialerSearchQueryParams } from "./use-search-query-params";
import { usePrefetchedConnectedLead } from "./use-prefetched-connected-lead";
import { findLeadRowCardOpener } from "./lead-row-click-through";

// Components
import { AccountDetailsModule } from "@/modules/pipeline/account-details";
import { MaxedDialerDataStates } from "./data-states";

// Constants
import {
  DIALER_CALL_STATUS,
  DIALER_MODES,
  DIALER_VERSIONS,
} from "@/constants/dialer";
import { LOG_CATEGORIES } from "@/constants/logs";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { WIDGETS } from "@/constants/widgets";
import { GLOBAL_DEBUG_TWILIO_INCOMING_CALL_RECEIVED_AT } from "@/constants/window-globals";

// Helpers & Utils
import { dd } from "@/helpers/datadog";
import { DayJs } from "shared/lib/helpers/date";
import { dispatchCustomEvent } from "@/helpers/events";
import { widgets } from "@/components/shared/widgets";

// Types & Interfaces
import { MaxedDialerWidgetConfigI } from "./interface";

const AccountDetailsSidebar: FC<MaxedDialerWidgetConfigI> = ({
  campaignId,
  accountId,
  tab,
  accountDetails,
}) => {
  const callStatus = useDialerCallStatus();
  const { isEnabled: isGlobalDialer } = useDialerGlobalContext();
  const cardRootRef = useRef<HTMLDivElement>(null);
  const pressedLeadRowCardOpenerRef = useRef<HTMLElement | null>(null);

  const accountDetailsProps = useAccountDetails(
    campaignId as string,
    accountId as string,
    accountDetails
  );

  const { accountDetailsApi } = accountDetailsProps;

  const {
    campaign,
    account,
    onAccountUpdate,
    isFetchingData,
    isInitialFetchComplete,
    isErrorOccurred,
  } = accountDetailsProps;

  const isAllowedToClose = useMemo(
    () => callStatus === DIALER_CALL_STATUS.BEFORE,
    [callStatus]
  );

  const handleClose = () => {
    /**
     * NOTE account details context is not available here
     * so we need to dispatch custom event to close the account details
     * based not just the call status but other criteria
     */
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.CLOSE,
    });
  };

  const handleErrorClose = () => {
    widgets.close({ id: WIDGETS.MAXED_DIALER });
  };

  /**
   * GLE-8457: a click on an empty card layer above a lead row opens that lead,
   * as long as the card may close. While the GC dialer runs, a row click does
   * nothing, so the card keeps its old behavior.
   */
  const isLeadRowClickable = isAllowedToClose && !isGlobalDialer;

  const getLeadRowCardOpener = (event: MouseEvent<HTMLDivElement>) =>
    isLeadRowClickable
      ? findLeadRowCardOpener(event, cardRootRef.current)
      : null;

  /**
   * NOTE keeps the card opener of the lead row under the press. A press on
   *      card content keeps nothing, so a drag from the card opens no lead.
   *      Stops the card layout from reading this press as a click outside the
   *      card, which would close it on mouse up
   */
  const handleMouseDownCapture = (event: MouseEvent<HTMLDivElement>) => {
    pressedLeadRowCardOpenerRef.current = getLeadRowCardOpener(event);

    if (pressedLeadRowCardOpenerRef.current) {
      event.stopPropagation();
    }
  };

  /**
   * NOTE a click that ends on the lead row of the press stops the overlay from
   *      closing the card, then clicks that row's card opener. A click after
   *      a press on a lead row that ends anywhere else does nothing
   */
  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const pressedLeadRowCardOpener = pressedLeadRowCardOpenerRef.current;

    pressedLeadRowCardOpenerRef.current = null;

    if (!pressedLeadRowCardOpener) {
      return;
    }

    event.stopPropagation();

    if (getLeadRowCardOpener(event) === pressedLeadRowCardOpener) {
      pressedLeadRowCardOpener.click();
    }
  };

  /**
   * For the max side of the sidebar we calculate max values for each column
   */
  return (
    <div
      ref={cardRootRef}
      onMouseDownCapture={handleMouseDownCapture}
      onClickCapture={handleClickCapture}
    >
      <Sidebar
        className="bg-none p-0"
        headerClassName="p-0 h-[0]"
        closeButtonClassName="absolute top-5 right-5"
        containerClassName="max-w-[calc(400px+354px+874px)] w-full shadow-none"
        isOverlayClickable={isAllowedToClose}
        hideTitle
        hideBg
        disableAnimation
        handleClose={handleClose}
        closeButton={CloseButton}
        hideCloseIcon
      >
        <DataStatesWrapper
          api={{
            isFetching: !isInitialFetchComplete && !!isFetchingData,
            isError: accountDetailsApi.isError || isErrorOccurred,
            isSuccess: accountDetailsApi.isSuccess && !isErrorOccurred,
            isEmpty: !account || !campaign,
          }}
          empty={
            <MaxedDialerDataStates.EmptyState
              account={account}
              campaign={campaign}
            />
          }
          error={
            <MaxedDialerDataStates.ErrorState
              onClose={handleErrorClose}
              onReload={onAccountUpdate}
            />
          }
          viewName="account"
        >
          <AccountDetailsModule tab={tab} {...accountDetailsProps} />
        </DataStatesWrapper>
      </Sidebar>
    </div>
  );
};

export const WidgetMaxedDialer: FC<MaxedDialerWidgetConfigI> = (props) => {
  const { isEnabled: isGlobalDialer } = useDialerGlobalContext();
  const {
    campaignId,
    accountId,
    contactId,
    connectedLead,
    accountDetails,
    phone,
    email,
  } = props;

  useMaxedDialerSearchQueryParams({
    campaignId,
    accountId,
    contactId,
    phone,
    email,
  });
  usePrefetchedConnectedLead(connectedLead, accountDetails);

  useEffect(() => {
    const twilioIncomingCallReceivedAt = (window as any)[
      GLOBAL_DEBUG_TWILIO_INCOMING_CALL_RECEIVED_AT
    ];
    const diff = !twilioIncomingCallReceivedAt
      ? 0
      : DayJs().diff(DayJs(twilioIncomingCallReceivedAt), "milliseconds");

    dd.rum.log(`${LOG_CATEGORIES.MAXED_DAILER} - widget is mounted`, {
      data: {
        props,
        measureLatencyTwilioIncomingEvent:
          diff > 1000 || diff < -1000 ? 0 : Math.abs(diff),
      },
    });

    (window as any)[GLOBAL_DEBUG_TWILIO_INCOMING_CALL_RECEIVED_AT] = undefined;
  }, []);

  return isGlobalDialer ? (
    <AccountDetailsSidebar {...props} />
  ) : (
    //In the scenario when Global Dialer is not enabled
    //Initializing Dialer Provider Locally
    <DialerV2Provider
      context={{
        //TODO Make it option it include spread of defaultContextObject
        ...defaultContextObject,
        campaignId,
      }}
      config={{
        version: DIALER_VERSIONS.V2,
        mode: DIALER_MODES.WIDGET_MAXED_DIALER,
      }}
    >
      <AccountDetailsSidebar {...props} />
    </DialerV2Provider>
  );
};
